import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { getSubscriberReceipts, sendSubscriberReceipt, whyNotSendable } from "../../api/subscriberReceipts";
import type { InvoicePrefSubscriber } from "../../api/invoicePrefs";
import { printVoucher } from "./voucherSlip";
import { rupees } from "../../api/cart";

/**
 * One subscriber's recharge receipts, and "Send to subscriber" for the one they
 * asked for (owner 2026-10-10). Sent from no-reply@ankhos.io on the operator's
 * behalf; radcomm reads the receipt and the verified address itself.
 */
export function SubscriberReceiptsDialog({ ns, subscriber, onClose }: { ns: string; subscriber: InvoicePrefSubscriber | null; onClose: () => void }) {
  const identity = useIdentity();
  const accountId = subscriber?.accountid ?? "";
  const list = useQuery({
    queryKey: ["subscriberreceipts", ns, accountId],
    enabled: Boolean(identity.token && ns && accountId),
    queryFn: ({ signal }) => getSubscriberReceipts(identity, ns, accountId, signal),
    retry: false,
  });
  const [sent, setSent] = useState<{ receiptno: string; to: string } | null>(null);
  const [blocked, setBlocked] = useState(false);
  const send = useMutation({
    mutationFn: (receiptno: string) => sendSubscriberReceipt(identity, ns, accountId, receiptno),
    onSuccess: (r) => setSent(r),
  });
  const close = () => {
    setSent(null);
    setBlocked(false);
    send.reset();
    onClose();
  };
  const data = list.data;

  return (
    <Dialog open={subscriber !== null} onClose={close} fullWidth maxWidth="md">
      <DialogTitle>Receipts</DialogTitle>
      {subscriber && (
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Typography variant="body2">{subscriber.name || subscriber.username || subscriber.accountid}</Typography>
          <Typography variant="caption" color="text.secondary">
            A receipt is sent only when the subscriber asks for it, from no-reply@ankhos.io on your behalf.
            {data?.emailmasked ? ` It goes to ${data.emailmasked}.` : ""}
          </Typography>
          {list.error && <Alert severity="error">{list.error instanceof Error ? list.error.message : String(list.error)}</Alert>}
          {blocked && <Alert severity="warning">The browser blocked the voucher window. Allow pop-ups for this site and press Print voucher again.</Alert>}
          {sent && <Alert severity="success">Receipt {sent.receiptno} sent to {sent.to}.</Alert>}
          {send.error && <Alert severity="error">{send.error instanceof Error ? send.error.message : String(send.error)}</Alert>}
          {data && data.receipts.length === 0 && <Alert severity="info">No receipts for this subscriber yet.</Alert>}
          {data && data.receipts.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Receipt</TableCell>
                  <TableCell>Date (IST)</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {data.receipts.map((r) => {
                  const why = whyNotSendable(r, data);
                  return (
                    <TableRow key={r.receiptno}>
                      <TableCell>
                        <code>{r.receiptno}</code>
                      </TableCell>
                      <TableCell>{r.time}</TableCell>
                      <TableCell>{r.kind === "other" ? "Other items" : r.plan}</TableCell>
                      <TableCell>{r.note || "—"}</TableCell>
                      <TableCell align="right">{rupees(r.amount)}</TableCell>
                      <TableCell align="right">
                        {!r.reversed && (
                          <Button size="small" onClick={() => setBlocked(!printVoucher({
                            provider: ns, name: subscriber.name || subscriber.username, mobile: subscriber.mobile || subscriber.username,
                            plan: r.plan, receiptno: r.receiptno, amount: r.amount, method: r.method, time: r.time,
                            ...(r.kind ? { kind: r.kind } : {}), ...(r.note ? { items: r.note } : {}),
                          }))}>
                            {r.kind === "other" ? "Print receipt" : "Print voucher"}
                          </Button>
                        )}
                        <Tooltip title={why}>
                          <span>
                            <Button size="small" disabled={Boolean(why) || send.isPending} onClick={() => send.mutate(r.receiptno)}>
                              Send to subscriber
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={close}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
